// LQC-2173 - the tier logic behind the Expires column's countdown. All pure and
// all take `now`, so the tiers, the day boundary and the colour thresholds are
// tested without fake timers. See docs/features/TRADE_EXPIRY.md.

import type { CSSProperties } from "react";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { returnStringFormattedDateTimeToMinute } from "@/lib/globalFormatterHelper";
import { formatExpiryCountdown } from "../../../tradeSettingsHelper/expiryDurationHelper";
import {
  expiryUrgentBackground,
  expiryWarningBackground,
} from "../../../statusCellStylingHelper";

// Seconds appear only inside this window. Deliberately tighter than the colour
// warning below: 15m is the typical short stamp, so a 15m seconds threshold
// would put nearly every stamped trade in the per-second tier for its whole
// life - exactly the churn the tiering exists to avoid.
export const SECONDS_TIER_MS = 5 * 60 * 1000;
export const WARNING_TONE_MS = 15 * 60 * 1000;
export const URGENT_TONE_MS = SECONDS_TIER_MS;

// Only rendered in the gap between the client clock passing zero and the backend
// status following - a settled trade reverts to a static timestamp (LQC-2366).
export const EXPIRING_LABEL = "Expiring ...";

// LQC-2678 - panel labels. A blank field reads as missing data, and a lapsed
// trade's raw timestamp doesn't say that it lapsed, so both get a word.
export const NO_EXPIRY_LABEL = "GTC";
export const EXPIRED_LABEL = "Expired";

export type ExpiryTier = "none" | "date" | "minutes" | "seconds" | "expired";
export type ExpiryTone = "none" | "warning" | "urgent";

export interface IExpiryDisplay {
  tier: ExpiryTier;
  label: string;
  tone: ExpiryTone;
}

// A trade only expires while it is still out and undealt. Anything awarded,
// executed, filled or closed keeps its expiryEnd on the record but must not
// tick, colour or trigger a reconcile - a settled trade counting down in orange
// is simply wrong.
//
// Recalled and ChangePending ARE live: the backend keeps expiring the CPs that
// are still out, so a panel edit that recalls one CP must not silently stop the
// countdown on the rest. Confirmed by a real trade, not assumed.
//
// Positive list on purpose: an unlisted status quietly does nothing, which is
// the safe failure mode.
export const STATUSES_WITH_LIVE_EXPIRY: string[] = [
  ExecutionStrategyStatus.Sent,
  ExecutionStrategyStatus.ReSent,
  ExecutionStrategyStatus.Received,
  ExecutionStrategyStatus.Acknowledged,
  ExecutionStrategyStatus.Quoted,
  ExecutionStrategyStatus.OffQuote,
  ExecutionStrategyStatus.ReQuoteRequested,
  ExecutionStrategyStatus.Working,
  ExecutionStrategyStatus.Recalled,
  ExecutionStrategyStatus.ChangePending,
];

export function isExpiryLive(status: string | undefined): boolean {
  if (!status) return false;
  return STATUSES_WITH_LIVE_EXPIRY.includes(status);
}

// Same calendar day in local time. Split on the day, NOT on a 24h threshold: at
// 18:00 today an expiry at 09:00 tomorrow is 15 hours out, so a 24h rule renders
// "15h 00m" and never reveals that it crosses midnight.
export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function resolveExpiryTier(
  expiryEnd: Date | null,
  nowMs: number,
  isLive: boolean,
): ExpiryTier {
  if (!expiryEnd) return "none";
  if (!isLive) return "none";
  const remainingMs = expiryEnd.getTime() - nowMs;
  if (remainingMs <= 0) return "expired";
  if (remainingMs <= SECONDS_TIER_MS) return "seconds";
  return isSameCalendarDay(expiryEnd, new Date(nowMs)) ? "minutes" : "date";
}

export function resolveExpiryTone(
  remainingMs: number,
  tier: ExpiryTier,
): ExpiryTone {
  if (tier === "none") return "none";
  // Past zero is the MOST urgent the row gets, not the least - it keeps the tint
  // until the backend confirms, then drops to tier "none" and loses it.
  if (tier === "expired") return "urgent";
  if (remainingMs <= URGENT_TONE_MS) return "urgent";
  if (remainingMs <= WARNING_TONE_MS) return "warning";
  return "none";
}

// The inert cell: a plain timestamp, no tick and no colour. Used for a trade
// that can no longer expire, and for the whole column when the
// tradeExpiryCountdown flag is off. Minute precision matches the date tier -
// the seconds are still one hover (and one copy) away.
export function returnStaticExpiryDisplay(
  expiryEnd: Date | null,
): IExpiryDisplay {
  return {
    tier: "none",
    label: expiryEnd ? returnStringFormattedDateTimeToMinute(expiryEnd) : "",
    tone: "none",
  };
}

export function returnExpiryDisplay(
  expiryEnd: Date | null,
  nowMs: number,
  isLive: boolean,
): IExpiryDisplay {
  const tier = resolveExpiryTier(expiryEnd, nowMs, isLive);
  if (!expiryEnd || tier === "none")
    return returnStaticExpiryDisplay(expiryEnd);

  const remainingMs = expiryEnd.getTime() - nowMs;
  const tone = resolveExpiryTone(remainingMs, tier);

  if (tier === "expired") return { tier, label: EXPIRING_LABEL, tone };
  if (tier === "date") {
    return {
      tier,
      label: returnStringFormattedDateTimeToMinute(expiryEnd),
      tone,
    };
  }

  return {
    tier,
    label: formatExpiryCountdown(remainingMs / 1000, tier === "seconds"),
    tone,
  };
}

// The subscription key. Quantised to whatever resolution the cell is actually
// displaying, so the label is rebuilt (and the cell re-rendered) only when the
// visible text can have changed. A cell counting down in days returns the same
// key every tick and never re-renders.
//
// Quantising on the REMAINING time rather than on the wall clock also makes the
// tier and tone boundaries exact: remaining/60000 crosses an integer at the
// moment remaining crosses a whole minute.
export function returnExpiryStepKey(
  expiryEnd: Date | null,
  nowMs: number,
  isLive: boolean,
): string {
  const tier = resolveExpiryTier(expiryEnd, nowMs, isLive);
  if (!expiryEnd || tier === "none") return "none";
  if (tier === "expired") return "expired";

  const remainingMs = expiryEnd.getTime() - nowMs;
  if (tier === "seconds") return `s:${Math.floor(remainingMs / 1000)}`;
  if (tier === "minutes") return `m:${Math.floor(remainingMs / 60000)}`;
  // Date tier shows a fixed timestamp, so the only thing that can change it is
  // the expiry becoming today. Key on the local calendar day: constant until
  // midnight, which is precisely when that can happen. A minute bucket here
  // would rebuild the same date string 1,440 times a day for nothing.
  return `d:${returnLocalDayIndex(nowMs)}`;
}

// Arithmetic only (no string building) - this runs per visible cell per tick.
function returnLocalDayIndex(nowMs: number): number {
  const local = new Date(nowMs);
  return local.getFullYear() * 10000 + local.getMonth() * 100 + local.getDate();
}

// Inline rather than a Tailwind class: the tint is derived from the shared
// timeToActionColour, not a palette entry.
export function returnExpiryToneStyle(
  tone: ExpiryTone,
): CSSProperties | undefined {
  if (tone === "none") return undefined;
  return {
    backgroundColor:
      tone === "urgent" ? expiryUrgentBackground : expiryWarningBackground,
  };
}
